/**
 * @file index.tsx
 *
 * @fileoverview Renders a single facet for a single selectable item. Receives a facet as prop. Will recursively
 * go through the items in the facet and set as active each that are represented in the selectedItems also
 * passed via props (These are calculated in the component that calls this one based on the url).
 * Only one item can be selected at a time in this type of facet (even though it can have multiple levels of nesting).
 */

import React, { Component } from 'react';

import { Facet, FacetValue } from '~/jobiqo/models/search';
import { checkFacetExists, getFacetLabel } from '../utils/facetHelpers';
import { FacetChildList } from '~/vcl/molecules/07-search/02-facet/01-facet-box';
import Radio from '~/vcl/atoms/01-forms/14-radios/01-radio';

interface Props {
  querystring: any;
  pathname?: string;
  facet: Facet;
  selectedItems: string[];
  onFacetToggle: (
    name: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
}

interface FacetListProps {
  facetValues: FacetValue[];
  isChild?: boolean;
}

export default class FacetSingle extends Component<Props> {
  /**
   * Method to help see if a filter is selected.
   */
  public checkFacetExists(filter: string): boolean {
    return checkFacetExists(
      filter,
      this.props.facet.values,
      this.props.selectedItems,
    );
  }

  /**
   * Return the facet label including counting.
   */
  public getFacetLabel(facetItem: FacetValue): string {
    return getFacetLabel(facetItem);
  }

  /**
   * Toggles the clicked facet value in single-select mode.
   *
   * If the value is already selected, it clears the facet; otherwise it selects only this value.
   * The optional alias is forwarded so URL/query handling can use the canonical facet key.
   *
   * @param facetItem The facet item that was clicked.
   */
  public handleFacetChange(facetItem: FacetValue): void {
    const selected = this.checkFacetExists(facetItem.filter);
    const alias = facetItem.alias || undefined;

    this.props.onFacetToggle(
      this.props.facet.name,
      selected ? [] : [facetItem.filter],
      alias,
    );
  }

  public RecursiveLinkList = (props: FacetListProps) => {
    const { facetValues, isChild } = props;

    return facetValues.map((facetItem) => {
      const selected = this.checkFacetExists(facetItem.filter);

      return (
        <FacetChildList key={facetItem.filter} isChild={isChild}>
          <Radio
            title={this.getFacetLabel(facetItem)}
            id={`radio-${facetItem.filter}`}
            name={`radio-${this.props.facet.name}`}
            value={facetItem.filter}
            checked={selected}
            onChange={() => this.handleFacetChange(facetItem)}
            onClick={() => {
              if (selected) {
                this.handleFacetChange(facetItem);
              }
            }}
          />
          {selected &&
            facetItem.children &&
            this.RecursiveLinkList({
              facetValues: facetItem.children,
              isChild: true,
            })}
        </FacetChildList>
      );
    });
  };

  public render() {
    const { facet } = this.props;
    return this.RecursiveLinkList({
      facetValues: facet.values,
      isChild: true,
    });
  }
}
